import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogFooter from "components/Dialog/DialogFooter.jsx";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Button from "components/CustomButtons/Button.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";
import CustomDatePicker from "components/DatePicker/CustomDatePicker.jsx";
import DataTable, { Pill, formatUGX } from "components/DataTable/DataTable.jsx";
import DialogHeader from "components/Dialog/DialogHeader.jsx";

import api from "api/index";
import ViewToolbar from "components/Toolbar/ViewToolbar.jsx";

const styles = {
  header: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  formControl: { width: "100%", marginTop: "16px" },
  errorMsg: { color: "#f44336", fontSize: "13px" },
  searchBar: { display: "flex", gap: "12px", marginBottom: "16px", alignItems: "flex-end" }
};

const CATEGORIES = ["Electronics", "Furniture", "Vehicle", "Equipment", "Building", "IT", "Laboratory", "Other"];
const CONDITIONS = ["Excellent", "Good", "Fair", "Poor", "Written Off"];
const CONDITION_TONES = {
  Excellent: "success",
  Good: "info",
  Fair: "warning",
  Poor: "danger",
  "Written Off": "muted"
};

const BLANK = { name: "", assetNo: "", category: "", condition: "Good", location: "", purchaseDate: "", purchaseValue: "", assignedTo: "" };

class AssetList extends Component {
  state = {
    assets: [],
    loading: true,
    search: "",
    categoryFilter: "",
    open: false,
    editing: null,
    form: { ...BLANK },
    saving: false,
    error: null
  };

  componentDidMount() { this.fetchAssets(); }

  fetchAssets = async () => {
    this.setState({ loading: true });
    try {
      const { search, categoryFilter } = this.state;
      const params = { limit: 100 };
      if (search) params.search = search;
      if (categoryFilter) params.category = categoryFilter;
      const res = await api.get("/assets", { params });
      const d = res.data.data || res.data;
      this.setState({ assets: Array.isArray(d) ? d : d.data || [], loading: false });
    } catch (e) { this.setState({ loading: false }); }
  };

  handleChange = field => e => { const v = e.target.value; this.setState(prev => ({ form: { ...prev.form, [field]: v } })); };

  handleSave = async () => {
    const { form, editing } = this.state;
    if (!form.name) { this.setState({ error: "Asset name is required." }); return; }
    this.setState({ saving: true, error: null });
    try {
      const payload = { ...form, purchaseValue: form.purchaseValue ? parseInt(form.purchaseValue, 10) : null };
      if (editing) await api.put(`/assets/${editing}`, payload);
      else await api.post("/assets", payload);
      this.setState({ open: false, saving: false });
      this.fetchAssets();
    } catch (e) {
      this.setState({ error: e.response?.data?.message || "Failed to save.", saving: false });
    }
  };

  handleDelete = async (id) => {
    if (!window.confirm("Write off / remove this asset?")) return;
    try {
      await api.delete(`/assets/${id}`);
      this.fetchAssets();
    } catch (e) { /* silent */ }
  };

  openEdit = (a) => {
    this.setState({
      open: true, editing: a.id, error: null,
      form: {
        name: a.name || "",
        assetNo: a.assetNo || "",
        category: a.category || "",
        condition: a.condition || "Good",
        location: a.location || "",
        purchaseDate: a.purchaseDate || "",
        purchaseValue: a.purchaseValue != null ? String(a.purchaseValue) : "",
        assignedTo: a.assignedTo || ""
      }
    });
  };

  render() {
    const { classes } = this.props;
    const { assets, loading, search, categoryFilter, open, editing, form, saving, error } = this.state;

    const columns = [
      { id: "assetNo", header: "Asset No", accessorFn: a => a.assetNo || "—", meta: { variant: "mono" } },
      { id: "name", header: "Name", accessorFn: a => a.name },
      { id: "category", header: "Category", accessorFn: a => a.category || "—" },
      { id: "location", header: "Location", accessorFn: a => a.location || "—" },
      {
        id: "condition",
        header: "Condition",
        cell: info => (
          <Pill tone={CONDITION_TONES[info.row.original.condition] || "muted"}>
            {info.row.original.condition || "Unknown"}
          </Pill>
        )
      },
      {
        id: "purchaseValue",
        header: "Purchase Value (UGX)",
        accessorFn: a => a.purchaseValue,
        cell: info => (info.getValue() != null ? formatUGX(info.getValue()) : "—"),
        meta: { align: "right" }
      },
      { id: "assignedTo", header: "Assigned To", accessorFn: a => a.assignedTo || "—" },
      {
        id: "actions",
        header: "",
        cell: info => (
          <div style={{ display: "flex", gap: "4px" }}>
            <Button size="sm" color="info" simple onClick={() => this.openEdit(info.row.original)}>Edit</Button>
            <Button size="sm" color="danger" simple onClick={() => this.handleDelete(info.row.original.id)}>Del</Button>
          </div>
        )
      }
    ];

    return (
      <GridContainer>
        <GridItem xs={12}>
          <ViewToolbar tabs={[]} actions={[]} />
          <div className={classes.searchBar}>
            <input
              type="text"
              placeholder="Search assets..."
              value={search}
              onChange={e => this.setState({ search: e.target.value })}
              onKeyDown={e => e.key === "Enter" && this.fetchAssets()}
              style={{ flex: 1, maxWidth: "300px", padding: "8px 12px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "14px" }}
            />
            <FormControl style={{ minWidth: "160px" }}>
              <InputLabel>Category</InputLabel>
              <Select value={categoryFilter} onChange={e => this.setState({ categoryFilter: e.target.value }, this.fetchAssets)}>
                <MenuItem value="">— All —</MenuItem>
                {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
            <Button size="sm" onClick={this.fetchAssets} color="primary" style={{ marginBottom: "2px" }}>
              Search
            </Button>
          </div>

          <DataTable
            title="Assets Register"
            subtitle="Institutional assets, condition, and assignments"
            actions={
              <Button size="sm" color="white" onClick={() => this.setState({ open: true, editing: null, form: { ...BLANK }, error: null })}>
                + Register Asset
              </Button>
            }
            columns={columns}
            data={assets}
            isLoading={loading}
            emptyState="No assets found."
          />
        </GridItem>

        <Dialog open={open} onClose={() => this.setState({ open: false })} maxWidth="sm" fullWidth>
          <DialogHeader title={editing ? "Edit Asset" : "Register New Asset"} />
          <DialogContent>
            {error && <p className={classes.errorMsg}>{error}</p>}
            <CustomInput labelText="Asset Name *" id="assetName" formControlProps={{ fullWidth: true }}
              inputProps={{ value: form.name, onChange: this.handleChange("name") }} />
            <CustomInput labelText="Asset No / Tag" id="assetNo" formControlProps={{ fullWidth: true }}
              inputProps={{ value: form.assetNo, onChange: this.handleChange("assetNo") }} />
            <GridContainer>
              <GridItem xs={6}>
                <FormControl className={classes.formControl}>
                  <InputLabel>Category</InputLabel>
                  <Select value={form.category} onChange={this.handleChange("category")}>
                    <MenuItem value="">— None —</MenuItem>
                    {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem xs={6}>
                <FormControl className={classes.formControl}>
                  <InputLabel>Condition</InputLabel>
                  <Select value={form.condition} onChange={this.handleChange("condition")}>
                    {CONDITIONS.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                  </Select>
                </FormControl>
              </GridItem>
            </GridContainer>
            <CustomInput labelText="Location / Room" id="assetLocation" formControlProps={{ fullWidth: true }}
              inputProps={{ value: form.location, onChange: this.handleChange("location") }} />
            <GridContainer>
              <GridItem xs={6}>
                <CustomDatePicker labelText="Purchase Date" id="purchaseDate" formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.purchaseDate, onChange: this.handleChange("purchaseDate") }} />
              </GridItem>
              <GridItem xs={6}>
                <CustomInput labelText="Purchase Value (UGX)" id="purchaseValue" formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.purchaseValue, onChange: this.handleChange("purchaseValue"), type: "number", min: "0" }} />
              </GridItem>
            </GridContainer>
            <CustomInput labelText="Assigned To" id="assignedTo" formControlProps={{ fullWidth: true }}
              inputProps={{ value: form.assignedTo, onChange: this.handleChange("assignedTo"), placeholder: "e.g. HOD Science" }} />
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={() => this.setState({ open: false })} disabled={saving}>Cancel</Button>
            <Button color="primary" onClick={this.handleSave} disabled={saving}>
              {saving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : "Save"}
            </Button>
          </DialogFooter>
        </Dialog>
      </GridContainer>
    );
  }
}

AssetList.propTypes = { classes: PropTypes.object.isRequired };
export default withRouter(withStyles(styles)(AssetList));
